import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Trash2, Send, Users, Link2, Copy, BarChart3 } from "lucide-react";
import { CohortTotals, CohortProgressTable } from "@/components/CohortProgress";
import api, { formatApiError } from "@/lib/api";
import { COUNTRIES } from "@/data/countries";

const EMPTY = { first_name: "", last_name: "", country: "", email: "" };

const parsePaste = (text) => text.split(/\r?\n/).map((l) => l.split(/[,;\t]/).map((x) => x.trim())).filter((p) => p.length >= 4 && p[3].includes("@"))
  .map(([first_name, last_name, country, email]) => ({ first_name, last_name, country, email }));

const MemberRow = ({ m, i, onChange, onRemove }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-[1fr_1fr_1fr_1.4fr_auto] gap-2 [&>*]:min-w-0 [&>*]:w-full" data-testid={`cohort-member-${i}`}>
    <input className="field" placeholder="Name" value={m.first_name} onChange={(e) => onChange({ ...m, first_name: e.target.value })} data-testid={`cohort-first-${i}`}/>
    <input className="field" placeholder="Surname" value={m.last_name} onChange={(e) => onChange({ ...m, last_name: e.target.value })} data-testid={`cohort-last-${i}`}/>
    <select className="field" value={m.country} onChange={(e) => onChange({ ...m, country: e.target.value })} data-testid={`cohort-country-${i}`}>
      <option value="">Country…</option>
      {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
      {m.country && !COUNTRIES.includes(m.country) && <option>{m.country}</option>}
    </select>
    <input className="field" type="email" placeholder="Email" value={m.email} onChange={(e) => onChange({ ...m, email: e.target.value })} data-testid={`cohort-email-${i}`}/>
    <button type="button" onClick={onRemove} className="chip text-xs text-red-700 justify-center" title="Remove" data-testid={`cohort-remove-${i}`}><Trash2 size={12}/></button>
  </div>
);

const EmailClientReport = ({ cohortId, contact, onSent }) => {
  const [f, setF] = useState({ to_email: contact?.email || "", contact_name: contact?.name || "", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!f.to_email.includes("@")) return toast.error("Enter the client contact email");
    setBusy(true);
    try { await api.post(`/admin/cohorts/${cohortId}/email-report`, f); toast.success(`Progress report emailed to ${f.to_email}`); onSent(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="pt-2 border-t border-[var(--border-warm)] space-y-2" data-testid={`cohort-email-report-${cohortId}`}>
      <div className="font-semibold inline-flex items-center gap-1"><Send size={14}/> Email report to client contact</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 [&>*]:min-w-0 [&>*]:w-full">
        <input className="field text-sm" placeholder="Contact name" value={f.contact_name} onChange={(e) => setF({ ...f, contact_name: e.target.value })} data-testid={`cohort-report-name-${cohortId}`}/>
        <input className="field text-sm" type="email" placeholder="Contact email" value={f.to_email} onChange={(e) => setF({ ...f, to_email: e.target.value })} data-testid={`cohort-report-email-${cohortId}`}/>
      </div>
      <textarea className="field w-full text-sm" rows={2} placeholder="Optional personal message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid={`cohort-report-message-${cohortId}`}/>
      <button type="button" disabled={busy} onClick={send} className="btn-terracotta px-4 py-2 rounded-full text-xs font-semibold disabled:opacity-60" data-testid={`cohort-report-send-${cohortId}`}>
        {busy ? "Sending…" : "Send progress report"}
      </button>
      {contact?.email && <div className="text-xs text-zinc-500">Last sent to {contact.email}</div>}
    </div>
  );
};

const CohortAutomation = ({ cohortId, data, onChange }) => {
  const [busy, setBusy] = useState(false);
  const live = ["CSCE", "CSP"].includes((data.course_code || "").split("-")[0]) && !(data.course_code || "").includes("-EXAM");
  const weekly = async (enabled) => {
    try { await api.post(`/admin/cohorts/${cohortId}/weekly-reports`, { enabled }); toast.success(enabled ? "Weekly Monday reports switched on" : "Weekly reports switched off"); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const invite = async () => {
    if (!window.confirm("Email every member of this cohort the upcoming live session dates and joining links?")) return;
    setBusy(true);
    try { const { data: r } = await api.post(`/admin/cohorts/${cohortId}/live-invite`); toast.success(`Live session invite sent to ${r.sent} member(s) (${r.sessions} session(s))`); onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="pt-2 border-t border-[var(--border-warm)] flex flex-wrap items-center gap-3" data-testid={`cohort-automation-${cohortId}`}>
      <label className="inline-flex items-center gap-2 text-xs font-semibold cursor-pointer">
        <input type="checkbox" checked={!!data.weekly_reports} onChange={(e) => weekly(e.target.checked)} data-testid={`cohort-weekly-${cohortId}`}/>
        Email client report every Monday
      </label>
      {live && (
        <button type="button" disabled={busy} onClick={invite} className="chip text-xs inline-flex items-center gap-1 disabled:opacity-60" data-testid={`cohort-live-invite-${cohortId}`}>
          <Send size={12}/> {busy ? "Sending…" : "Send live session invite"}
        </button>
      )}
      {data.live_invite_sent_at && <span className="text-xs text-zinc-500">Live invite last sent {data.live_invite_sent_at.slice(0, 10)}</span>}
    </div>
  );
};

const CohortProgressPanel = ({ cohortId }) => {
  const [data, setData] = useState(null);
  const load = () => api.get(`/admin/cohorts/${cohortId}/progress`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [cohortId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="text-xs text-zinc-500 py-2">Loading progress…</div>;
  const link = data.share_token ? `${window.location.origin}/cohort-progress/${data.share_token}` : "";
  const share = async (enabled) => {
    if (!enabled && !window.confirm("Switch off the client link? Anyone with the old link will lose access.")) return;
    try { await api.post(`/admin/cohorts/${cohortId}/share`, { enabled }); toast.success(enabled ? "Client link created" : "Client link switched off"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3 mt-3" data-testid={`cohort-progress-${cohortId}`}>
      <div className="eyebrow inline-flex items-center gap-1"><BarChart3 size={12}/> Progress</div>
      <CohortTotals t={data.totals}/>
      <CohortProgressTable rows={data.rows} showEmail/>
      <div className="rounded-xl border border-[var(--border-warm)] bg-[var(--cream)] p-3 text-sm space-y-2" data-testid={`cohort-share-${cohortId}`}>
        <div className="font-semibold inline-flex items-center gap-1"><Link2 size={14}/> Client progress link (read-only, no emails shown)</div>
        {link ? (
          <div className="flex flex-wrap items-center gap-2">
            <input readOnly value={link} className="field flex-1 min-w-0 text-xs" data-testid={`cohort-share-link-${cohortId}`}/>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Link copied"); }} className="chip text-xs inline-flex items-center gap-1" data-testid={`cohort-share-copy-${cohortId}`}><Copy size={12}/> Copy</button>
            <button type="button" onClick={() => share(false)} className="chip text-xs text-red-700" data-testid={`cohort-share-off-${cohortId}`}>Switch off</button>
          </div>
        ) : (
          <button type="button" onClick={() => share(true)} className="chip text-xs" data-testid={`cohort-share-on-${cohortId}`}>Create client link</button>
        )}
        <EmailClientReport cohortId={cohortId} contact={data.client_contact} onSent={load}/>
        <CohortAutomation cohortId={cohortId} data={data} onChange={load}/>
      </div>
    </div>
  );
};

const CohortList = ({ cohorts }) => {
  const [open, setOpen] = useState({});
  const resend = async (c, m) => {
    try {
      const { data } = await api.post(`/admin/cohorts/${c.id}/members/${m.user_id}/resend`);
      toast.success(data.new_temporary_password ? "Welcome email resent with a new temporary password" : "Welcome email resent");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (!cohorts.length) return <div className="card-warm p-5 text-sm text-zinc-500" data-testid="cohorts-empty">No cohorts onboarded yet.</div>;
  return (
    <div className="space-y-3" data-testid="cohorts-list">
      {cohorts.map((c) => (
        <details key={c.id} className="card-warm p-4 min-w-0" onToggle={(e) => { const isOpen = e.currentTarget?.open ?? e.target?.open ?? false; setOpen((o) => ({ ...o, [c.id]: isOpen })); }} data-testid={`cohort-${c.id}`}>
          <summary className="cursor-pointer flex flex-wrap items-center gap-2 text-sm">
            <b>{c.name}</b><span className="text-zinc-500">· {c.company}{c.po_number ? ` · PO ${c.po_number}` : ""} · {c.course_title} · {c.members.length} members · {(c.created_at || "").slice(0, 10)}</span>
          </summary>
          <div className="mt-3 divide-y divide-[var(--border-warm)]">
            {c.members.map((m) => (
              <div key={m.user_id} className="py-2 flex flex-wrap items-center justify-between gap-2 text-sm">
                <div className="min-w-0">
                  <div className="font-semibold">{m.first_name} {m.last_name} <span className="font-normal text-zinc-500">· {m.country}</span></div>
                  <div className="text-xs text-zinc-600 break-all">{m.email} · {m.account === "created" ? "new account" : "existing account"} · {m.enrolment === "enrolled" ? "enrolled" : "already enrolled"}</div>
                </div>
                <button onClick={() => resend(c, m)} className="chip text-xs inline-flex items-center gap-1" data-testid={`cohort-resend-${m.user_id}`}><Send size={12}/> Resend welcome</button>
              </div>
            ))}
          </div>
          {open[c.id] && <CohortProgressPanel cohortId={c.id}/>}
        </details>
      ))}
    </div>
  );
};

export const CohortRegistrationTab = () => {
  const [courses, setCourses] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [form, setForm] = useState({ name: "", company: "", po_number: "", course_id: "" });
  const [members, setMembers] = useState([{ ...EMPTY }]);
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/cohorts").then((r) => setCohorts(r.data || [])).catch(() => {});
  useEffect(() => { api.get("/admin/courses").then((r) => setCourses(r.data || [])).catch(() => {}); load(); }, []);
  const course = useMemo(() => courses.find((c) => c.id === form.course_id), [courses, form.course_id]);
  const valid = members.filter((m) => m.first_name && m.last_name && m.country && m.email.includes("@"));

  const addPasted = () => {
    const rows = parsePaste(paste);
    if (!rows.length) return toast.error("Use one line per person: Name, Surname, Country, email");
    setMembers([...members.filter((m) => m.email || m.first_name), ...rows]); setPaste("");
    toast.success(`${rows.length} member(s) added`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!valid.length) return toast.error("Add at least one complete member (Name, Surname, Country, email)");
    if (!window.confirm(`Register ${valid.length} member(s) from ${form.company} and enrol them in ${course?.title}? Enrolment is approved automatically (PO customer) and welcome emails with temporary passwords are sent.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/cohorts", { ...form, members: valid });
      const created = data.members.filter((m) => m.account === "created").length;
      toast.success(`Cohort registered: ${data.members.length} member(s), ${created} new account(s). Welcome emails sent.`);
      setMembers([{ ...EMPTY }]); setForm({ name: "", company: "", po_number: "", course_id: form.course_id }); load();
    } catch (ex) { toast.error(formatApiError(ex.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-6 min-w-0 max-w-full" data-testid="admin-registration-tab">
      <form onSubmit={submit} className="card-warm p-4 sm:p-5 space-y-4 min-w-0 overflow-hidden">
        <div>
          <h3 className="font-display text-2xl inline-flex items-center gap-2"><Users size={20}/> Onboard a business cohort</h3>
          <p className="text-sm text-zinc-600 mt-1">For business customers paying by purchase order. Accounts are created with temporary passwords, enrolment is approved automatically and each member receives a welcome email.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 [&>*]:min-w-0 [&>*]:w-full">
          <input className="field" required placeholder="Cohort name (e.g. Acme Bank - Nov 2026)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cohort-name"/>
          <input className="field" required placeholder="Company / business" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="cohort-company"/>
          <input className="field" placeholder="PO number (optional)" value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} data-testid="cohort-po"/>
          <select className="field" required value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} data-testid="cohort-course">
            <option value="">Choose course…</option>
            {courses.map((c) => <option key={c.id} value={c.id}>{c.code ? `${c.code} - ` : ""}{c.title}</option>)}
          </select>
        </div>
        {course && (
          <div className="text-xs rounded-xl bg-[var(--cream)] border border-[var(--border-warm)] p-3" data-testid="cohort-email-preview">
            Welcome email will say: {["CSCE", "CSP"].includes((course.code || "").split("-")[0]) && !(course.code || "").includes("-EXAM") && !course.is_exam_only
              ? "a separate email with live session details will follow" : "the examination is available on the course page now"}; all study materials are downloadable on the course page; change the temporary password at first login.
          </div>
        )}
        <div className="space-y-2">
          <div className="eyebrow">Members ({valid.length} complete)</div>
          {members.map((m, i) => (
            <MemberRow key={i} m={m} i={i} onChange={(nm) => setMembers(members.map((x, j) => (j === i ? nm : x)))} onRemove={() => setMembers(members.length > 1 ? members.filter((_, j) => j !== i) : [{ ...EMPTY }])}/>
          ))}
          <button type="button" onClick={() => setMembers([...members, { ...EMPTY }])} className="chip text-xs inline-flex items-center gap-1" data-testid="cohort-add-member"><UserPlus size={12}/> Add member</button>
        </div>
        <div>
          <textarea className="field w-full" rows={3} placeholder={"Or paste many: one per line\nName, Surname, Country, email"} value={paste} onChange={(e) => setPaste(e.target.value)} data-testid="cohort-paste"/>
          <button type="button" onClick={addPasted} className="chip text-xs mt-2" data-testid="cohort-paste-add">Add pasted members</button>
        </div>
        <button disabled={busy || !form.course_id} className="btn-terracotta px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="cohort-submit">
          {busy ? "Registering…" : `Register & enrol ${valid.length || ""} member(s)`}
        </button>
      </form>
      <div>
        <div className="eyebrow mb-2">Onboarded cohorts</div>
        <CohortList cohorts={cohorts}/>
      </div>
    </div>
  );
};
